import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { downloadExcel } from "react-export-table-to-excel";
import { BiArrowBack, BiSearch, BiListUl, BiCheckCircle, BiUndo, BiPackage } from "react-icons/bi";

import { fetchGet, fetchPost } from "../../fetchers";
import { Colors } from "../../assets/Colors";
import { HrdInputGenerator } from "../../components/form/HrdInputGenerator";
import { useForm } from "../../components/isRequired";

// Gudang › PO Transit — PO Barang (sejak 1 Jan 2026) yang belum diterima seluruhnya.
// Definisinya sama dengan kolom "Belum Diterima" di /gudang/stock dan "Qty Transit" di MSR.
// "Adjustment" menganggap PO sudah diterima semua TANPA menambah qty di stok
// (hanya tercatat di POTransitAdjustment).

const PAGE_SIZE = 50;
const fmt = (n) => new Intl.NumberFormat("id-ID", { maximumFractionDigits: 2 }).format(n ?? 0);
const fmtTgl = (s) => (s ? `${s.slice(8, 10)}-${s.slice(5, 7)}-${s.slice(0, 4)}` : "");

function DetailModal({ po, adjusted = false, onClose }) {
  const mutation = useMutation((q) =>
    fetchGet((adjusted ? "Procurement/POTransit/adjusted/detail" : "Procurement/POTransit/detail") + q));
  useEffect(() => {
    mutation.mutate(`?po=${encodeURIComponent(po.PurchaseOrderID)}`, {
      onError: () => toast.error("Gagal memuat detail PO"),
    });
  }, [po.PurchaseOrderID]); // eslint-disable-line
  const rows = mutation.data?.data ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={onClose}>
      <div className="w-full max-w-4xl max-h-[85vh] flex flex-col bg-white rounded-xl shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-bold text-gray-800">{po.PurchaseOrderID}</h3>
          <p className="text-xs text-gray-500">
            {po.NamaSupplier || "-"} · Tgl PO {fmtTgl(po.TanggalPO)} · Requester {po.Requester || "-"}
          </p>
        </div>
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-white sticky top-0" style={{ background: Colors.primary }}>
                <th className="px-4 py-3">Kode</th>
                <th className="px-4 py-3">Nama Barang</th>
                <th className="px-4 py-3">Satuan</th>
                <th className="px-4 py-3 text-right">Qty PO</th>
                <th className="px-4 py-3 text-right">Diterima</th>
                <th className="px-4 py-3 text-right">{adjusted ? "Di-adjust" : "Belum Diterima"}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">{mutation.isLoading ? "Memuat…" : "Tidak ada data"}</td></tr>
              ) : rows.map((r, i) => (
                <tr key={r.PurchaseOrderListID} className={i % 2 ? "bg-gray-50" : "bg-white"}>
                  <td className="px-4 py-2 font-mono text-xs text-gray-700">{r.BarangID}</td>
                  <td className="px-4 py-2 text-gray-800">{r.NamaBarang}</td>
                  <td className="px-4 py-2 text-gray-600">{r.NamaSatuan}</td>
                  <td className="px-4 py-2 text-right">{fmt(r.QtyPO)}</td>
                  <td className="px-4 py-2 text-right">{fmt(r.QtyDiterima)}</td>
                  <td className="px-4 py-2 text-right font-semibold text-orange-600">{fmt(r.QtySisa)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-3 border-t flex justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50">Tutup</button>
        </div>
      </div>
    </div>
  );
}

function AdjustModal({ po, onClose, onDone }) {
  const [keterangan, setKeterangan] = useState("");
  const mutation = useMutation((body) => fetchPost("Procurement/POTransit/adjust", body));
  const submit = () => {
    if (!keterangan.trim()) { toast.warn("Alasan adjustment wajib diisi"); return; }
    mutation.mutate({ PurchaseOrderID: po.PurchaseOrderID, Keterangan: keterangan.trim() }, {
      onSuccess: () => { toast.success(`${po.PurchaseOrderID} di-adjust`); onDone(); },
    });
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-xl shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-bold text-gray-800">Adjustment {po.PurchaseOrderID}</h3>
        </div>
        <div className="px-6 py-4 space-y-3 text-sm text-gray-700">
          <p>
            Seluruh sisa PO ini (<strong>{fmt(po.QtySisa)}</strong> pada <strong>{po.JumlahItem}</strong> item) akan
            dianggap <strong>sudah diterima</strong> dan hilang dari PO Transit, kolom Belum Diterima, dan Qty Transit MSR.
          </p>
          <p className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded p-2">
            Qty di /gudang/stock <strong>tidak bertambah</strong>, dan tidak ada Penerimaan Barang atau jurnal yang dibuat.
          </p>
          <div>
            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">Alasan</label>
            <textarea value={keterangan} onChange={(e) => setKeterangan(e.target.value)} rows={3}
              placeholder="Mis. barang sudah diterima tetapi penerimaannya tidak diinput"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
          </div>
        </div>
        <div className="px-6 py-3 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50">Batal</button>
          <button onClick={submit} disabled={mutation.isLoading}
            className="px-4 py-2 text-sm rounded-lg text-white disabled:opacity-50" style={{ background: Colors.primary }}>
            {mutation.isLoading ? "Memproses…" : "Adjust"}
          </button>
        </div>
      </div>
    </div>
  );
}

function BatalModal({ po, onClose, onDone }) {
  const [keterangan, setKeterangan] = useState("");
  const mutation = useMutation((body) => fetchPost("Procurement/POTransit/batal-adjust", body));
  const submit = () => {
    if (!keterangan.trim()) { toast.warn("Alasan pembatalan wajib diisi"); return; }
    mutation.mutate({ PurchaseOrderID: po.PurchaseOrderID, Keterangan: keterangan.trim() }, {
      onSuccess: () => { toast.success(`Adjustment ${po.PurchaseOrderID} dibatalkan`); onDone(); },
    });
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-xl shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-bold text-gray-800">Batalkan Adjustment {po.PurchaseOrderID}</h3>
        </div>
        <div className="px-6 py-4 space-y-3 text-sm text-gray-700">
          <p>
            Sisa PO sebesar <strong>{fmt(po.QtySisa)}</strong> pada <strong>{po.JumlahItem}</strong> item akan kembali
            tercatat sebagai <strong>belum diterima</strong> (muncul lagi di PO Transit, kolom Belum Diterima, dan Qty Transit MSR).
            Qty di /gudang/stock tidak berubah.
          </p>
          <div>
            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">Alasan</label>
            <textarea value={keterangan} onChange={(e) => setKeterangan(e.target.value)} rows={3}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
          </div>
        </div>
        <div className="px-6 py-3 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50">Kembali</button>
          <button onClick={submit} disabled={mutation.isLoading}
            className="px-4 py-2 text-sm rounded-lg text-white bg-red-600 disabled:opacity-50">
            {mutation.isLoading ? "Memproses…" : "Batalkan Adjustment"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function POTransitPage() {
  const navigate = useNavigate();
  const [bisnisUnit, setBisnisUnit] = useState("U0002");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [detailPO, setDetailPO] = useState(null);
  const [adjustPO, setAdjustPO] = useState(null);
  const [batalPO, setBatalPO] = useState(null);
  const [tab, setTab] = useState("belum"); // "belum" | "adjusted"
  const adjusted = tab === "adjusted";

  const { values, errors, touched, customHandler } = useForm({ BisnisUnitID: "U0002" }, []);
  useEffect(() => {
    if (values.BisnisUnitID) { setBisnisUnit(values.BisnisUnitID); setPage(1); }
  }, [values.BisnisUnitID]);

  const mutation = useMutation((q) =>
    fetchGet((adjusted ? "Procurement/POTransit/adjusted" : "Procurement/POTransit") + q));
  const load = () =>
    mutation.mutate(`?bisnisunit=${encodeURIComponent(bisnisUnit)}`, {
      onError: () => toast.error("Gagal memuat PO Transit"),
    });
  useEffect(() => { setSearch(""); setPage(1); load(); }, [bisnisUnit, tab]); // eslint-disable-line

  const items = useMemo(() => mutation.data?.data ?? [], [mutation.data]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((r) =>
      (r.PurchaseOrderID || "").toLowerCase().includes(q) ||
      (r.NamaSupplier || "").toLowerCase().includes(q) ||
      (r.Requester || "").toLowerCase().includes(q));
  }, [items, search]);
  const maxPage = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleExcel = () => {
    if (filtered.length === 0) { toast.info("Tidak ada data untuk diunduh"); return; }
    downloadExcel({
      fileName: `po-transit${adjusted ? "-adjusted" : ""}_${bisnisUnit}`,
      sheet: "PO Transit",
      tablePayload: adjusted ? {
        header: ["No. PO", "Tanggal PO", "Supplier", "Item", "Qty Di-adjust", "Alasan", "Oleh", "Tanggal Adjust"],
        body: filtered.map((r) => [r.PurchaseOrderID, fmtTgl(r.TanggalPO), r.NamaSupplier,
          r.JumlahItem, r.QtySisa, r.Keterangan, r.DiadjustOleh, r.TanggalAdjust]),
      } : {
        header: ["No. PO", "Tanggal PO", "Supplier", "Requester", "Item Belum Lengkap", "Qty PO", "Diterima", "Belum Diterima"],
        body: filtered.map((r) => [r.PurchaseOrderID, fmtTgl(r.TanggalPO), r.NamaSupplier, r.Requester,
          r.JumlahItem, r.QtyPO, r.QtyDiterima, r.QtySisa]),
      },
    });
  };

  return (
    <div className="md:ml-64">
      <div style={{ background: Colors.primary }} className="pt-14 pb-20 px-8" />
      <div className="px-4 md:px-8 -mt-16 pb-16">
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b">
            <div className="flex items-center gap-3 mb-4">
              <button onClick={() => navigate("/gudang")} className="text-gray-500 hover:text-gray-800">
                <BiArrowBack className="text-xl" />
              </button>
              <div>
                <h2 className="text-lg font-bold text-gray-800">PO Transit</h2>
                <p className="text-xs text-gray-500">
                  PO Barang (sejak 1 Januari 2026) yang belum diterima seluruhnya atau masih ada barang yang belum diterima.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 items-end">
              <div className="w-full md:w-64">
                <HrdInputGenerator type="BisnisUnit" name="BisnisUnitID" title="Bisnis Unit" value={values}
                  required={true} onChange={customHandler} touched={touched} errors={errors} size="full" />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              {[["belum", "Belum Diterima"], ["adjusted", "Sudah di-Adjust"]].map(([k, label]) => (
                <button key={k} onClick={() => setTab(k)}
                  className={`px-4 py-2 text-sm rounded-lg border ${tab === k ? "text-white" : "bg-white text-gray-600 border-gray-300"}`}
                  style={tab === k ? { background: Colors.primary, borderColor: Colors.primary } : undefined}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3 border-b bg-gray-50">
            <div className="relative w-full max-w-sm">
              <BiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
              <input type="text" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Cari No. PO, supplier, requester…"
                className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg outline-none bg-white" />
            </div>
            <button onClick={handleExcel} className="h-10 px-4 rounded-lg border text-sm font-medium bg-white"
              style={{ borderColor: Colors.primary, color: Colors.primary }}>
              Download Excel
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-white" style={{ background: Colors.primary }}>
                  <th className="px-4 py-3 w-14">No</th>
                  <th className="px-4 py-3">No. PO</th>
                  <th className="px-4 py-3">Tgl PO</th>
                  <th className="px-4 py-3">Supplier</th>
                  {adjusted ? (
                    <>
                      <th className="px-4 py-3 text-right">Item</th>
                      <th className="px-4 py-3 text-right">Qty Di-adjust</th>
                      <th className="px-4 py-3">Alasan</th>
                      <th className="px-4 py-3">Oleh / Tanggal</th>
                    </>
                  ) : (
                    <>
                      <th className="px-4 py-3">Requester</th>
                      <th className="px-4 py-3 text-right">Item Belum Lengkap</th>
                      <th className="px-4 py-3 text-right">Qty PO</th>
                      <th className="px-4 py-3 text-right">Diterima</th>
                      <th className="px-4 py-3 text-right">Belum Diterima</th>
                    </>
                  )}
                  <th className="px-4 py-3 text-right">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 ? (
                  <tr><td colSpan={10} className="px-4 py-10 text-center text-gray-400">
                    {mutation.isLoading ? "Memuat…" : adjusted ? "Tidak ada PO yang di-adjust" : "Tidak ada PO yang belum diterima"}
                  </td></tr>
                ) : pageRows.map((r, i) => (
                  <tr key={r.PurchaseOrderID} className={i % 2 ? "bg-gray-50" : "bg-white"}>
                    <td className="px-4 py-2 text-gray-500">{(page - 1) * PAGE_SIZE + i + 1}</td>
                    <td className="px-4 py-2 font-medium text-gray-800 whitespace-nowrap">{r.PurchaseOrderID}</td>
                    <td className="px-4 py-2 text-gray-600 whitespace-nowrap">{fmtTgl(r.TanggalPO)}</td>
                    <td className="px-4 py-2 text-gray-700">{r.NamaSupplier || "-"}</td>
                    {adjusted ? (
                      <>
                        <td className="px-4 py-2 text-right">{r.JumlahItem}</td>
                        <td className="px-4 py-2 text-right font-semibold text-gray-800">{fmt(r.QtySisa)}</td>
                        <td className="px-4 py-2 text-gray-700">{r.Keterangan || "-"}</td>
                        <td className="px-4 py-2 text-gray-600 whitespace-nowrap">{r.DiadjustOleh} · {r.TanggalAdjust}</td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-2 text-gray-700">{r.Requester || "-"}</td>
                        <td className="px-4 py-2 text-right">{r.JumlahItem}</td>
                        <td className="px-4 py-2 text-right">{fmt(r.QtyPO)}</td>
                        <td className="px-4 py-2 text-right">{fmt(r.QtyDiterima)}</td>
                        <td className="px-4 py-2 text-right font-semibold text-orange-600">{fmt(r.QtySisa)}</td>
                      </>
                    )}
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <button onClick={() => setDetailPO(r)} title="Lihat barang yang belum diterima"
                        className="inline-flex items-center gap-1 px-3 py-1.5 mr-2 rounded-lg border text-xs font-medium text-gray-600 border-gray-300 hover:bg-gray-50">
                        <BiListUl className="text-sm" /> Detail
                      </button>
                      {adjusted ? (
                        <button onClick={() => setBatalPO(r)} title="Kembalikan sisa PO sebagai belum diterima"
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border text-xs font-medium text-red-600 border-red-300 hover:bg-red-50">
                          <BiUndo className="text-sm" /> Batalkan Adjustment
                        </button>
                      ) : (
                        <>
                        <button
                          onClick={() => navigate(`/gudang/transaction/form-penerimaan-barang-add/${bisnisUnit}/${encodeURIComponent(r.PurchaseOrderID)}`)}
                          title="Buat Penerimaan Barang untuk PO ini"
                          className="inline-flex items-center gap-1 px-3 py-1.5 mr-2 rounded-lg text-xs font-medium text-white"
                          style={{ background: Colors.primary }}>
                          <BiPackage className="text-sm" /> Buat Penerimaan
                        </button>
                        <button onClick={() => setAdjustPO(r)} title="Anggap PO sudah diterima semua (stok tidak bertambah)"
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border text-xs font-medium hover:bg-orange-50"
                          style={{ borderColor: Colors.primary, color: Colors.primary }}>
                          <BiCheckCircle className="text-sm" /> Adjustment
                        </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between px-6 py-3 border-t bg-gray-50 text-xs text-gray-600">
            <span>{filtered.length} PO{search ? ` (dari ${items.length})` : ""}</span>
            <div className="flex items-center gap-2">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1 rounded border bg-white disabled:opacity-40">Prev</button>
              <span>Halaman {page} / {maxPage}</span>
              <button disabled={page >= maxPage} onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1 rounded border bg-white disabled:opacity-40">Next</button>
            </div>
          </div>
        </div>
      </div>

      {detailPO && <DetailModal po={detailPO} adjusted={adjusted} onClose={() => setDetailPO(null)} />}
      {batalPO && (
        <BatalModal po={batalPO} onClose={() => setBatalPO(null)}
          onDone={() => { setBatalPO(null); load(); }} />
      )}
      {adjustPO && (
        <AdjustModal po={adjustPO} onClose={() => setAdjustPO(null)}
          onDone={() => { setAdjustPO(null); load(); }} />
      )}
    </div>
  );
}
